import Select from '../Select';
import TextInput from '../TextInput';
import TrashIcon from '../icons/TrashIcon.jsx';
import ColorPickerGrid from '../ColorPickerGrid.jsx';
import { useEffect, useMemo, useState } from 'react';
import SortableContainers from '../SortableContainers.jsx';
import Toggle from '../Toggle.jsx';
import { ObjectTypes } from '~/lib/ObjectTypes';
import ErrorMessage from '~/components/ErrorMessage';

const fieldTypes = {
    text: 'Single Line Text',
    paragraph: 'Paragraph',
    email: 'Email',
    dropdown: 'Dropdown',
    yesno: 'Yes/No',
    formfield: 'Form Field',
};

const initialColors = ['#CCCCCC'];

const ColorPreview = ({ color, onClick, className }) => {
    return (
        <div
            className={`w-5 h-5 rounded cursor-pointer ${className}`}
            style={{ backgroundColor: color }}
            onClick={onClick}
        ></div>
    );
};

const DropdownOption = ({ opt, onChange, onDelete, onEnter }) => {
    const [showColorPicker, setShowColorPicker] = useState(false);
    return (
        <>
            <div className="flex items-center space-x-4 py-2">
                <ColorPreview
                    color={opt.color}
                    onClick={() => setShowColorPicker(true)}
                />
                <TextInput
                    placeholder="Option name"
                    className="flex-1"
                    value={opt.label}
                    autoFocus={!opt.label}
                    onKeyDown={event => event.key === 'Enter' && onEnter()}
                    onChange={newValue => onChange({ ...opt, label: newValue })}
                />
                <button
                    type="button"
                    className="w-5 h-5 text-red-600"
                    onClick={onDelete}
                >
                    <TrashIcon />
                </button>
            </div>
            {showColorPicker && (
                <ColorPickerGrid
                    value={opt.color}
                    onChange={newValue => {
                        onChange({ ...opt, color: newValue });
                        setShowColorPicker(false);
                    }}
                />
            )}
        </>
    );
};

const DropdownOptions = ({ options, onChange, className }) => {
    options = options || [];

    function addOption() {
        onChange([
            ...options,
            {
                color: initialColors[options.length % initialColors.length],
                label: '',
            },
        ]);
    }

    useEffect(() => {
        if (options.length === 0) {
            addOption();
        }
    }, []);

    return (
        <div className={className}>
            <div className="input-label">Options</div>
            {options.map((opt, idx) => (
                <DropdownOption
                    opt={opt}
                    onEnter={addOption}
                    onChange={newOpt => {
                        const newOptions = [...options];
                        newOptions[idx] = newOpt;
                        onChange(newOptions);
                    }}
                    onDelete={() => {
                        const newOptions = [...options];
                        newOptions.splice(idx, 1);
                        onChange(newOptions);
                    }}
                />
            ))}

            <button
                type="button"
                className="btn-light-pill-sm mt-2"
                onClick={() => {
                    addOption();
                }}
            >
                Add Option
            </button>
        </div>
    );
};

const SingleFieldSchema = ({ value, setValue, type }) => {
    const [showColorPicker, setShowColorPicker] = useState(false);

    const fieldTypeOptions = useMemo(() => {
        if (type === ObjectTypes.CLIENT || type === ObjectTypes.BOOKING_EVENT) {
            return fieldTypes;
        }

        let opts = {};
        Object.keys(fieldTypes)
            .filter(t => t !== 'formfield')
            .forEach(t => (opts[t] = fieldTypes[t]));

        return opts;
    }, [type]);

    return (
        <div>
            <TextInput
                label="Label"
                className="mt-4"
                value={value?.label}
                autoFocus={!value?.label}
                onChange={newValue =>
                    setValue(old => ({ ...old, label: newValue }))
                }
            />

            <Select
                label="Type"
                value={value?.type}
                onChange={newValue =>
                    setValue(old => ({ ...old, type: newValue }))
                }
                options={fieldTypeOptions}
                className="mt-4"
            />

            {value?.type === 'formfield' && (
                <div className="mt-1 text-gray-500 text-sm mx-2">
                    The value for this will come from the most recent form field
                    with a question label matching the label above,{' '}
                    <span className="font-semibold">{value?.label}</span>
                    {!value?.label && (
                        <ErrorMessage>Please set a label above</ErrorMessage>
                    )}
                </div>
            )}

            {value?.type === 'dropdown' && (
                <DropdownOptions
                    className="mt-4"
                    options={value?.options}
                    onChange={newValue =>
                        setValue(old => ({ ...old, options: newValue }))
                    }
                />
            )}

            {value?.type === 'yesno' && (
                <div className="mt-4">
                    <div className="input-label">Color</div>
                    <div
                        onClick={() => setShowColorPicker(!showColorPicker)}
                        className="cursor-pointer"
                    >
                        {!value?.color && 'None'}
                        <div className="flex items-center space-x-4">
                            <ColorPreview
                                color={value?.color}
                                onClick={() => null}
                            />
                            {!!value?.color && (
                                <div className="text-sm text-gray-500">
                                    Color applies when "Yes" answer is selected
                                </div>
                            )}
                        </div>
                    </div>

                    {showColorPicker && (
                        <ColorPickerGrid
                            value={value?.color}
                            onChange={newValue => {
                                setValue(old => ({ ...old, color: newValue }));
                                setShowColorPicker(false);
                            }}
                        />
                    )}
                </div>
            )}

            <label className="flex items-center mt-4">
                <Toggle
                    className="mr-4"
                    enabled={!!value?.is_tag}
                    setEnabled={isEnabled =>
                        setValue(old => ({ ...old, is_tag: isEnabled }))
                    }
                />
                <div>Show as a tag</div>
            </label>
            {!!value?.is_tag && (
                <div className="text-gray-500 text-sm mt-1">
                    This will show in summary views
                </div>
            )}
        </div>
    );
};

export default SingleFieldSchema;
